import { Heart, Phone, Home, DollarSign, Users, Scale } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import { useLanguage } from "@/hooks/useLanguage";
import { familyContent } from "@/i18n/family-content";

const Divorce = () => {
  const { language } = useLanguage();
  const content = familyContent.divorce[language];

  const aiKeyPoints = [
    "Experienced divorce attorneys in Houston, Texas",
    "Handle uncontested and contested divorces",
    "Community property division and asset protection",
    "Child custody, child support, and spousal support",
    "Texas requires 60-day waiting period from filing",
    "Trilingual service: English, Spanish, Vietnamese"
  ];

  const aiFaqs = [
    {
      question: "How long does a divorce take in Texas?",
      answer: "Texas requires a minimum 60-day waiting period from filing. Uncontested divorces can be finalized shortly after. Contested divorces may take 6-12 months or longer depending on complexity."
    },
    {
      question: "How is property divided in a Texas divorce?",
      answer: "Texas is a community property state. Property acquired during marriage is generally divided 'just and right' - usually 50/50, but courts can deviate based on factors like fault, earning capacity, and child custody."
    },
    {
      question: "How is child custody determined in Texas?",
      answer: "Texas courts prioritize the child's best interests. Factors include each parent's ability to care for the child, emotional needs, stability, and any history of family violence. Joint custody is preferred unless there are concerns."
    }
  ];

  return (
    <GenericPracticePage
      title={content.title}
      subtitle={content.subtitle}
      icon={<Heart className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      category="family"
      aiKeyPoints={aiKeyPoints}
      aiFaqs={aiFaqs}
      aiServiceDescription="Compassionate divorce attorneys in Houston, Texas. We handle all aspects of divorce including property division, child custody, child support, and spousal maintenance. Trilingual support available."
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">{content.sections[0].title}</h2>
            <p className="text-lg text-muted-foreground mb-4">
              {content.sections[0].content}
            </p>
            <p className="text-lg text-muted-foreground">
              Our goal is to achieve the best possible outcome for you while minimizing conflict and emotional stress. When negotiation doesn't work, we're experienced trial attorneys ready to fight for you in court.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Divorce in Texas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <h3 className="text-xl font-bold mb-2">Uncontested Divorce</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Both spouses agree on all major issues: property, debts, child custody, and support. Faster and less expensive.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Minimal court appearances</li>
                  <li>• Lower legal fees</li>
                  <li>• Quicker resolution (60+ days)</li>
                  <li>• Less emotional stress</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <h3 className="text-xl font-bold mb-2">Contested Divorce</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Spouses disagree on custody, property division, or support. Requires negotiation, mediation, or trial.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Complex property disputes</li>
                  <li>• Child custody battles</li>
                  <li>• Discovery and depositions</li>
                  <li>• Trial preparation and litigation</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Key Issues in Texas Divorce</h2>
            <div className="space-y-6">
              <div className="bg-gradient-subtle p-6 rounded-lg">
                <div className="flex items-start gap-4">
                  <Home className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Property Division (Community Property)</h3>
                    <p className="text-muted-foreground mb-3">
                      Texas is a community property state. All property and debts acquired during marriage are presumed to be community property and divided "in a just and right manner" - usually 50/50, but courts can deviate.
                    </p>
                    <div className="bg-card p-4 rounded mt-3">
                      <p className="text-sm font-semibold mb-2">Separate Property vs. Community Property:</p>
                      <p className="text-sm text-muted-foreground mb-2"><strong>Separate:</strong> Property owned before marriage, gifts, inheritances (yours to keep)</p>
                      <p className="text-sm text-muted-foreground"><strong>Community:</strong> Earned or acquired during marriage (subject to division)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-subtle p-6 rounded-lg">
                <div className="flex items-start gap-4">
                  <Users className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Child Custody (Conservatorship)</h3>
                    <p className="text-muted-foreground mb-3">
                      Texas courts prefer joint managing conservatorship (joint custody) unless there's evidence of family violence, neglect, or abuse. One parent is typically designated the primary residence.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Factors considered:</strong> Child's emotional and physical needs, parental abilities, stability of home, parental cooperation, child's preference (if 12+), history of family violence
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-subtle p-6 rounded-lg">
                <div className="flex items-start gap-4">
                  <DollarSign className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Child Support & Spousal Support</h3>
                    <p className="text-muted-foreground mb-3">
                      <strong>Child Support:</strong> Calculated based on non-custodial parent's net income and number of children. Texas guidelines: 20% for one child, 25% for two, etc.
                    </p>
                    <p className="text-muted-foreground">
                      <strong>Spousal Support (Alimony):</strong> Not automatic in Texas. Requires marriage of 10+ years or specific circumstances (family violence, disability). Limited in duration and amount.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">The Texas Divorce Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Filing the Petition</h3>
                  <p className="text-sm text-muted-foreground">One spouse files a Petition for Divorce. The other spouse must be served and has time to respond.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Temporary Orders (if needed)</h3>
                  <p className="text-sm text-muted-foreground">Court can set temporary child custody, support, and property use while divorce is pending.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Discovery & Disclosure</h3>
                  <p className="text-sm text-muted-foreground">Both sides exchange financial documents, answer questions, and provide full disclosure of assets and debts.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Negotiation & Mediation</h3>
                  <p className="text-sm text-muted-foreground">Most divorces settle through negotiation or mediation without going to trial.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">5</div>
                <div>
                  <h3 className="font-bold mb-1">Trial (if no settlement)</h3>
                  <p className="text-sm text-muted-foreground">If settlement fails, the case goes to trial where a judge decides all contested issues.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">6</div>
                <div>
                  <h3 className="font-bold mb-1">Final Decree</h3>
                  <p className="text-sm text-muted-foreground">Court issues Final Decree of Divorce, which is legally binding and enforceable.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">60-Day Waiting Period</h2>
            <p className="text-muted-foreground mb-4">
              Texas law requires a 60-day waiting period from the date the divorce petition is filed until the divorce can be finalized. This is a mandatory cooling-off period.
            </p>
            <p className="text-muted-foreground">
              However, preparation, negotiation, and resolution typically take much longer - especially in contested cases. The sooner you start, the better positioned you'll be.
            </p>
          </section>

          <section className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Protecting Yourself During Divorce</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Document all assets, debts, and income</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Don't hide assets or dissipate marital property</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Keep detailed records of child-related expenses</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Don't disparage the other parent to children</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Follow all temporary orders exactly</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Don't post about your divorce on social media</span>
              </div>
            </div>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Considering Divorce?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today for a confidential consultation. We'll discuss your situation, explain your options, and develop a strategy to protect your interests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default Divorce;
